import { createColumnHelper } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'
import {
  EOB_CLASSIFICATIONS,
  FINISH_CLASSIFICATION_CATEGORIES,
  FINISH_CLASSIFICATION_CATEGORIES_LEGACY,
  getCategoryMetadata,
  NOT_APPLICABLE,
  useSubcategories,
  type Subcategory,
} from '~/entities/categories'
import { extractInboxName } from '~/entities/data-sources'
import {
  DOCUMENT_SERVER_STATUS,
  getDocumentStep,
  getDocumentUrl,
  isTaggingClassification,
  PRIOR_AUTH_PHASES,
  type DocumentStep,
  type UIDocumentStep,
} from '~/entities/document'
import { FlagCell } from '~/entities/document-flags'
import { usePayors } from '~/entities/payors'
import { USIndicator } from '~/entities/reviewer'
import { useUserStore } from '~/entities/user'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { formatDateDefault } from '~/shared/lib/datetime'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { Button, DataTable, LocalIcon, Text } from '~/shared/ui'
import { TABLE_COLUMNS, TABLE_COLUMNS_MAP } from '../../config'
import { useFilters, usePaginatedDocuments, useTableSettings } from '../../model'
import { StatusChip } from '../status-chip'
import { StepTracker } from '../step-tracker'
import { KeyHeader } from './key-header'

const columnHelper = createColumnHelper<apiClient.Document>()

const getColumns = ({
  rowDisabledFn,
  subcategories,
  handleSortFn,
  sortBy,
  sortOrder,
  payors,
  isUserOnshore,
  hiddenColumns,
  isEobEnabled,
  isHideLlmClassificationEnabled,
}: {
  rowDisabledFn: (row: apiClient.Document) => boolean
  subcategories: Subcategory[]
  handleSortFn: (column: keyof typeof TABLE_COLUMNS_MAP) => void
  sortBy: keyof typeof TABLE_COLUMNS_MAP
  sortOrder: string
  payors: apiClient.Payor[]
  isUserOnshore: boolean
  hiddenColumns: (keyof typeof TABLE_COLUMNS_MAP)[]
  isEobEnabled: boolean
  isHideLlmClassificationEnabled: boolean
}) => [
  ...(hiddenColumns.includes(TABLE_COLUMNS.KEY)
    ? []
    : [
        columnHelper.accessor('key', {
          header: () => <KeyHeader />,
          cell: (info) => (
            <div
              className={cn('flex items-center gap-2.5', {
                '-ml-3': isUserOnshore,
              })}
            >
              {isUserOnshore && (
                <USIndicator
                  className={cn({ invisible: !info.row.original.onshore_only })}
                  align="start"
                />
              )}
              <div className="flex flex-col whitespace-nowrap">
                <Text
                  className="uppercase"
                  appearance="BODY2"
                >
                  {info.getValue()}
                </Text>
                <Text
                  className="text-text-secondary uppercase"
                  appearance="CAPTION"
                >
                  {info.row.original.cns_key}
                </Text>
              </div>
            </div>
          ),
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.DATE_RECEIVED)
    ? []
    : [
        columnHelper.accessor('inbox_info.date', {
          header: () => {
            return (
              <div className="flex items-center">
                {TABLE_COLUMNS_MAP.dateReceived.label}
                <button
                  className="cursor-pointer border-none bg-transparent px-1"
                  type="button"
                  onClick={() => handleSortFn(TABLE_COLUMNS.DATE_RECEIVED)}
                >
                  <LocalIcon
                    className={cn('ml-2 h-4.5 w-4.5', {
                      'text-content-high-emphasis fill-content-medium-emphasis':
                        sortBy === TABLE_COLUMNS.DATE_RECEIVED,
                      'text-content-medium-emphasis fill-content-high-emphasis':
                        sortBy === TABLE_COLUMNS.DATE_RECEIVED && sortOrder === 'desc',
                    })}
                    name="sort"
                  />
                </button>
              </div>
            )
          },
          cell: (info) => {
            const value = info.getValue()
            if (!value) return ''
            return <Text appearance="SUBTITLE2">{formatDateDefault(value)}</Text>
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.SOURCE)
    ? []
    : [
        columnHelper.accessor('inbox_name', {
          header: TABLE_COLUMNS_MAP.source.label,
          cell: (info) => {
            const inbox = info.getValue()
            if (!inbox) return ''
            const { name, description } = extractInboxName(inbox)
            return (
              <div className="flex flex-col">
                <Text appearance="SUBTITLE2">{`${name ?? ''}`}</Text>
                <Text appearance="CAPTION">
                  <span
                    className={cn('text-text-secondary', {
                      'text-text-disabled': rowDisabledFn(info.row.original),
                    })}
                  >
                    {description}
                  </span>
                </Text>
              </div>
            )
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.PAYOR)
    ? []
    : [
        columnHelper.accessor('payor_id', {
          header: TABLE_COLUMNS_MAP.payor.label,
          cell: (info) => {
            const payorId = info.getValue()
            if (!payorId) return ''
            const payor = payors.find((p) => p.payorId === payorId)
            return (
              <div className="flex flex-col">
                <Text
                  className="w-fit"
                  appearance="SUBTITLE2"
                >
                  {payorId}
                </Text>
                {payor?.payorName && (
                  <Text appearance="CAPTION">
                    <span
                      className={cn('text-text-secondary', {
                        'text-text-disabled': rowDisabledFn(info.row.original),
                      })}
                    >
                      {payor?.payorName}
                    </span>
                  </Text>
                )}
              </div>
            )
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.CLASSIFICATION)
    ? []
    : [
        columnHelper.accessor('category', {
          header: TABLE_COLUMNS_MAP.classification.label,
          cell: (info) => {
            const category = info.getValue()
            if (!category) return ''
            const categoryMetadata = getCategoryMetadata({ category, subcategories })
            if (!categoryMetadata)
              return (
                <Text
                  className="text-text-secondary"
                  appearance="BODY2"
                >
                  {category}
                </Text>
              )
            const hideDetails =
              isHideLlmClassificationEnabled &&
              PRIOR_AUTH_PHASES.includes(info.row.original.category_phase ?? '') &&
              info.row.original.review_status === DOCUMENT_SERVER_STATUS.pending
            const outcome =
              !hideDetails &&
              categoryMetadata.outcome &&
              categoryMetadata.outcome.label !== NOT_APPLICABLE
                ? categoryMetadata.outcome.displayLabel
                : ''
            const reason =
              outcome && categoryMetadata.reason && categoryMetadata.reason.label !== NOT_APPLICABLE
                ? `: ${categoryMetadata.reason.displayLabel}`
                : ''
            return (
              <div className="flex flex-col">
                <Text appearance="SUBTITLE2">{categoryMetadata.phase.displayLabel}</Text>
                {(outcome || reason) && (
                  <Text appearance="CAPTION">
                    <span
                      className={cn('text-text-secondary', {
                        'text-text-disabled': rowDisabledFn(info.row.original),
                      })}
                    >{`${outcome}${reason}`}</span>
                  </Text>
                )}
              </div>
            )
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.STEP)
    ? []
    : [
        columnHelper.accessor('reviewed_step', {
          header: TABLE_COLUMNS_MAP.step.label,
          cell: (info) => {
            const hasReviewer = !!info.row.original.reviewer
            const finishOnClassification = (
              isEobEnabled
                ? FINISH_CLASSIFICATION_CATEGORIES
                : FINISH_CLASSIFICATION_CATEGORIES_LEGACY
            ).includes(info.row.original.category ?? '')
            const isEobClassification =
              isEobEnabled &&
              EOB_CLASSIFICATIONS.includes(
                info.row.original.category as (typeof EOB_CLASSIFICATIONS)[number],
              )
            const isTaggingEnabled = isTaggingClassification(
              info.row.original.category,
              subcategories,
            )
            let step: UIDocumentStep | DocumentStep = getDocumentStep(
              info.row.original,
              subcategories,
            )
            if (
              info.row.original.review_status === DOCUMENT_SERVER_STATUS.reviewed ||
              info.row.original.review_status === DOCUMENT_SERVER_STATUS.auto_reviewed
            ) {
              step = 'finished'
            }
            if (info.row.original.review_status === DOCUMENT_SERVER_STATUS.processing) {
              step = 'processing'
            }
            return (
              <StepTracker
                step={step}
                hasReviewer={hasReviewer}
                maxStepsCount={finishOnClassification ? 4 : isEobClassification ? 10 : undefined}
                isTaggingEnabled={isTaggingEnabled}
                isEob={isEobClassification}
              />
            )
          },
        }),
      ]),
  ...(!isEobEnabled || hiddenColumns.includes(TABLE_COLUMNS.FLAGGED)
    ? []
    : [
        columnHelper.accessor('flags', {
          header: TABLE_COLUMNS_MAP.flagged.label,
          cell: (info) => {
            const flags = info.getValue()
            if (!flags?.length) return null
            return <FlagCell flags={flags} />
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.REVIEWER)
    ? []
    : [
        columnHelper.accessor('reviewer', {
          header: TABLE_COLUMNS_MAP.reviewer.label,
          cell: (info) => {
            const reviewer = info.getValue()

            if (!reviewer) {
              return (
                <Text
                  className="text-text-secondary"
                  appearance="BODY2"
                >
                  Awaiting reviewer
                </Text>
              )
            }
            return (
              <div className="flex flex-col">
                <Text appearance="BODY2">
                  {reviewer.first_name} {reviewer.last_name}
                </Text>
                <Text
                  className="text-text-secondary"
                  appearance="CAPTION"
                >
                  {reviewer.onshore ? 'US based' : 'International'}
                </Text>
              </div>
            )
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.STATUS)
    ? []
    : [
        columnHelper.accessor('review_status', {
          header: TABLE_COLUMNS_MAP.status.label,
          cell: (info) => {
            const status = info.getValue()
            if (!status) return ''
            return (
              <StatusChip
                status={status as apiClient.ReviewStatusEnum}
                hasReviewer={!!info.row.original.reviewer}
              />
            )
          },
        }),
      ]),
]

export function DocumentsTable() {
  const { data } = usePaginatedDocuments()
  const { filters, setFilters, resetFilters } = useFilters()
  const userInfo = useUserStore((state) => state.userInfo)
  const isOnshore = useUserStore((state) => state.isOnshore)
  const subcategories = useSubcategories()
  const {
    data: {
      data: { insurancePayors: payors },
    },
  } = usePayors()
  const { hiddenColumns } = useTableSettings()
  const isEobEnabled = useIsFeatureEnabled('2026-02-eob-phase-1')
  const isHideLlmClassificationEnabled = useIsFeatureEnabled('2026-08-hide-llm-classification')

  const rowDisabledFn = useCallback(
    (row: apiClient.Document) => {
      return (
        row.review_status === DOCUMENT_SERVER_STATUS.pending &&
        !!row.reviewer &&
        row.reviewer.sub !== userInfo?.email
      )
    },
    [userInfo?.email],
  )

  const handleSortFn = useCallback(
    (column: keyof typeof TABLE_COLUMNS_MAP) => {
      if (filters.sortBy === column) {
        setFilters({ sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })
      } else {
        setFilters({ sortBy: column, sortOrder: 'asc' })
      }
    },
    [filters.sortBy, filters.sortOrder, setFilters],
  )

  const columns = useMemo(
    () =>
      getColumns({
        rowDisabledFn,
        subcategories,
        handleSortFn,
        sortBy: filters.sortBy as keyof typeof TABLE_COLUMNS_MAP,
        sortOrder: filters.sortOrder,
        payors,
        isUserOnshore: isOnshore,
        hiddenColumns,
        isEobEnabled,
        isHideLlmClassificationEnabled,
      }),
    [
      rowDisabledFn,
      subcategories,
      handleSortFn,
      filters.sortBy,
      filters.sortOrder,
      payors,
      isOnshore,
      hiddenColumns,
      isEobEnabled,
      isHideLlmClassificationEnabled,
    ],
  )

  return (
    <DataTable
      columns={columns}
      data={data.items}
      containerClassName="[&_>_div]:flex [&_>_div]:h-full [&_>_div]:flex-col [&_>_div]:justify-between"
      withStickyHeader
      getRowUrl={(row: apiClient.Document) => {
        return getDocumentUrl(row, subcategories)
      }}
      noResultsMessage="Looks like your filters are a bit too specific – or maybe the docs just haven't arrived."
      noResultsAction={<Button onClick={resetFilters}>Clear filters</Button>}
      disabledRowTip={(row: apiClient.Document) => {
        if (rowDisabledFn(row)) {
          return `${row.reviewer?.first_name} is working on this document and it is currently locked.`
        }
        return null
      }}
    />
  )
}
